import { useState, useEffect, useCallback } from 'react';
import {
    Dialog, DialogContent, Box, Typography, Button,
    IconButton, CircularProgress, ThemeProvider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import { darkTheme } from '../../../theme/darkTheme';
import { PremiumApi } from '../../../services/api';

// ─── Keyframes ────────────────────────────────────────────────────────────────
const STYLES = `
  @keyframes goldShimmer {
    0%   { background-position: -200% center; }
    100% { background-position:  200% center; }
  }
  @keyframes crownFloat {
    0%, 100% { transform: translateY(0px) rotate(-4deg); }
    50%       { transform: translateY(-5px) rotate(4deg); }
  }
  @keyframes successPop {
    0%   { transform: scale(0.6); opacity: 0; }
    70%  { transform: scale(1.1); opacity: 1; }
    100% { transform: scale(1); }
  }
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(10px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes shimmerSweep {
    0%   { left: -100%; }
    100% { left: 200%; }
  }
  @keyframes pulseRing {
    0%   { box-shadow: 0 0 0 0 rgba(212,175,55,0.35); }
    70%  { box-shadow: 0 0 0 10px rgba(212,175,55,0); }
    100% { box-shadow: 0 0 0 0 rgba(212,175,55,0); }
  }
`;

if (typeof document !== 'undefined' && !document.getElementById('premium-modal-styles')) {
    const tag = document.createElement('style');
    tag.id = 'premium-modal-styles';
    tag.textContent = STYLES;
    document.head.appendChild(tag);
}

// ─── Palette ──────────────────────────────────────────────────────────────────
const C = {
    bg0: '#07101f',
    bg1: '#0f172a',
    bg2: '#1e293b',
    bg3: '#243044',
    border: 'rgba(212,175,55,0.22)',
    borderSubtle: 'rgba(255,255,255,0.07)',
    gold: '#D4AF37',
    goldLight: '#F0D060',
    goldDim: 'rgba(212,175,55,0.6)',
    green: '#22c55e',
    text: '#e2e8f0',
    textMuted: '#94a3b8',
    textFaint: '#475569',
    blue0: '#1e3a8a',
    blue1: '#3b82f6',
};

const shimmerText = {
    display: 'inline-block',
    background: `linear-gradient(75deg, ${C.gold} 15%, ${C.goldLight} 45%, #fffde8 50%, ${C.goldLight} 55%, ${C.gold} 85%)`,
    backgroundSize: '200% auto',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    animation: 'textShimmer 10s ease-in-out infinite',
    transition: 'background-position 0.5s ease',
    '@keyframes textShimmer': {
        '0%': {
            backgroundPosition: '200% center',
        },
        '100%': {
            backgroundPosition: '-200% center',
        },
    },
};

const primaryBtn = {
    height: 50,
    borderRadius: '14px',
    background: `linear-gradient(135deg, ${C.blue0} 0%, ${C.blue1} 100%)`,
    color: '#fff',
    fontWeight: 700,
    fontSize: '0.9rem',
    textTransform: 'none',
    letterSpacing: '0.01em',
    boxShadow: '0 4px 16px rgba(59,130,246,0.25)',
    transition: 'all 0.25s ease',
    '&:hover': {
        background: `linear-gradient(135deg, ${C.blue1} 0%, ${C.blue0} 100%)`,
        boxShadow: '0 6px 22px rgba(59,130,246,0.38)',
        transform: 'translateY(-2px)',
    },
    '&:disabled': { opacity: 0.4, transform: 'none' },
};

const goldBtn = {
    height: 52,
    borderRadius: '14px',
    background: `linear-gradient(135deg, #92700e 0%, ${C.gold} 25%, ${C.goldLight} 50%, ${C.gold} 75%, #92700e 100%)`,
    backgroundSize: '200% auto',
    color: '#07101f',
    fontWeight: 800,
    fontSize: '0.95rem',
    textTransform: 'none',
    letterSpacing: '0.02em',
    boxShadow: '0 4px 15px rgba(212, 175, 55, 0.3)',
    position: 'relative',
    overflow: 'hidden',
    transition: 'all 8s cubic-bezier(0.175, 0.885, 0.32, 1.275), background-position 4s ease',
    animation: 'goldMove 4s ease-in-out infinite alternate',
    '&:hover': {
        transform: 'translateY(-3px) scale(1.02)',
        boxShadow: '0 12px 30px rgba(212, 175, 55, 0.6)',
        backgroundPosition: 'right center',
    },
    '&:active': {
        transform: 'translateY(-1px) scale(0.99)',
        boxShadow: '0 4px 10px rgba(212, 175, 55, 0.4)',
    },
    '@keyframes goldMove': {
        '0%': {
            backgroundPosition: '0% 50%',
        },
        '50%': {
            backgroundPosition: '100% 50%',
        },
        '100%': {
            backgroundPosition: '0% 50%',
        },
    },
};

const PLANS = {
    monthly: {
        label: 'Monatlich',
        price: '1,99 €',
        priceValue: '1.99',
        sub: 'pro Monat',
        badge: null,
    },
    yearly: {
        label: 'Jährlich',
        price: '14,99 €',
        priceValue: '14.99',
        sub: 'pro Jahr · ≈1,25 €/Monat',
        badge: '37% Ersparnis',
    },
};

const FEATURES = [
    { label: 'Gewichtstracker', icon: '⚖️' },
    { label: 'Ernährungsplan', icon: '🥗' },
    { label: 'Fortschrittsfotos', icon: '📸' },
    { label: 'Statistiken & Auswertungen', icon: '📊' },
    { label: 'Unbegrenzte Custom-Pläne', icon: '♾️' },
    { label: 'Daten exportieren (PDF/CSV)', icon: '📥' },
];

function FeaturePill({ icon, label, delay = 0 }) {
    return (
        <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1,
            px: 1.5, py: 0.9,
            borderRadius: '10px',
            bgcolor: `${C.gold}0d`,
            border: `1px solid ${C.border}`,
            animation: `fadeUp 0.3s ${delay}s ease both`,
            transition: 'border-color 0.2s',
            '&:hover': { borderColor: C.goldDim },
        }}>
            <Typography sx={{ fontSize: '0.95rem', lineHeight: 1 }}>{icon}</Typography>
            <Typography sx={{ fontSize: '0.75rem', color: C.textMuted, fontWeight: 500, whiteSpace: 'nowrap' }}>
                {label}
            </Typography>
        </Box>
    );
}

function PlanCard({ planKey, plan, selected, onSelect }) {
    const isSelected = selected === planKey;
    return (
        <Box
            onClick={() => onSelect(planKey)}
            sx={{
                flex: 1,
                position: 'relative',
                px: 2, py: 2,
                borderRadius: '14px',
                border: '2px solid',
                borderColor: isSelected ? C.gold : C.borderSubtle,
                bgcolor: isSelected ? `${C.gold}0f` : C.bg3,
                cursor: 'pointer',
                transition: 'all 0.22s ease',
                boxShadow: isSelected ? `0 0 20px ${C.gold}22` : 'none',
                animation: isSelected ? 'pulseRing 1.8s ease' : 'none',
                '&:hover': {
                    borderColor: isSelected ? C.gold : `${C.gold}55`,
                    bgcolor: isSelected ? `${C.gold}12` : `${C.gold}07`,
                },
            }}
        >
            {isSelected && (
                <Box sx={{
                    position: 'absolute', top: 8, right: 8,
                    width: 18, height: 18, borderRadius: '50%',
                    bgcolor: C.gold,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: C.bg0 }} />
                </Box>
            )}
            {plan.badge && (
                <Box sx={{
                    position: 'absolute', top: -10, left: '50%',
                    transform: 'translateX(-50%)',
                    px: 1.25, py: 0.2,
                    borderRadius: '20px',
                    background: 'linear-gradient(135deg, #15803d, #22c55e)',
                    whiteSpace: 'nowrap',
                }}>
                    <Typography sx={{ fontSize: '0.62rem', fontWeight: 800, color: '#fff', letterSpacing: '0.04em' }}>
                        {plan.badge}
                    </Typography>
                </Box>
            )}
            <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: isSelected ? C.gold : C.textMuted, mb: 0.5, transition: 'color 0.2s' }}>
                {plan.label}
            </Typography>
            <Typography sx={{ fontWeight: 800, fontSize: '1.35rem', color: isSelected ? C.goldLight : C.text, lineHeight: 1.1, transition: 'color 0.2s' }}>
                {plan.price}
            </Typography>
            <Typography sx={{ fontSize: '0.68rem', color: C.textFaint, mt: 0.3 }}>
                {plan.sub}
            </Typography>
        </Box>
    );
}

// ─── Gemeinsamer Header (wird in Dialog UND Fullscreen-Overlay verwendet) ──────
function ModalHeader({ onClose, showClose }) {
    return (
        <Box sx={{
            px: 3, pt: 3, pb: 2.5,
            background: `linear-gradient(135deg, ${C.bg2} 0%, ${C.bg1} 100%)`,
            borderBottom: `1px solid ${C.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            position: 'relative', flexShrink: 0,
        }}>
            <Box sx={{
                position: 'absolute', top: -40, right: -40,
                width: 160, height: 160, borderRadius: '50%',
                background: `radial-gradient(circle, ${C.gold}1a 0%, transparent 70%)`,
                pointerEvents: 'none',
            }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{
                    width: 44, height: 44, borderRadius: '12px',
                    background: `linear-gradient(135deg, #92700e 0%, ${C.gold} 50%, ${C.goldLight} 100%)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: `0 4px 16px ${C.gold}44`,
                    animation: 'pulseRing 3s ease-in-out infinite',
                }}>
                    <WorkspacePremiumIcon sx={{ fontSize: '1.4rem', color: C.bg0, animation: 'crownFloat 3s ease-in-out infinite' }} />
                </Box>
                <Box>
                    <Typography sx={{ ...shimmerText, fontSize: '1.15rem', fontWeight: 800, letterSpacing: '-0.01em' }}>
                        GymApp Premium
                    </Typography>
                    <Typography sx={{ fontSize: '0.72rem', color: C.textFaint, mt: 0.1 }}>
                        Schalte alle Features frei
                    </Typography>
                </Box>
            </Box>
            {showClose && (
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{
                        color: C.textFaint,
                        border: `1px solid ${C.borderSubtle}`,
                        borderRadius: '8px',
                        width: 32, height: 32,
                        '&:hover': { borderColor: C.border, color: C.gold, bgcolor: `${C.gold}0d` },
                        transition: 'all 0.2s',
                    }}
                >
                    <CloseIcon sx={{ fontSize: '1rem' }} />
                </IconButton>
            )}
        </Box>
    );
}

// ─── Payment-Step als natives Fullscreen-Overlay ───────────────────────────────
// Komplett außerhalb des Dialog-Stacks → kein iframe-Clipping möglich
function PaymentOverlay({ open, plan, loading, onBack, createOrder, onApprove, onError, onCancel }) {
    if (!open) return null;
    return (
        <Box sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1400, // über dem Dialog (1300)
            bgcolor: C.bg1,
            display: 'flex',
            flexDirection: 'column',
            animation: 'fadeUp 0.2s ease both',
        }}>
            <ModalHeader onClose={onBack} showClose={false} />

            {/* Scrollbarer Inhalt */}
            <Box sx={{ flex: 1, overflowY: 'auto', p: 2.5 }}>
                {/* Zurück-Zeile */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2.5 }}>
                    <IconButton
                        onClick={onBack}
                        size="small"
                        sx={{
                            color: C.textMuted,
                            border: `1px solid ${C.borderSubtle}`,
                            borderRadius: '8px',
                            width: 36, height: 36,
                            '&:hover': { borderColor: C.border, color: C.gold },
                            transition: 'all 0.2s',
                        }}
                    >
                        <ArrowBackIcon sx={{ fontSize: '1.1rem' }} />
                    </IconButton>
                    <Box sx={{
                        flex: 1, px: 2, py: 1,
                        borderRadius: '10px',
                        bgcolor: C.bg2,
                        border: `1px solid ${C.border}`,
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    }}>
                        <Typography sx={{ fontSize: '0.8rem', color: C.textMuted, fontWeight: 500 }}>
                            {PLANS[plan].label}
                        </Typography>
                        <Typography sx={{ ...shimmerText, fontSize: '0.9rem', fontWeight: 800 }}>
                            {PLANS[plan].price}
                        </Typography>
                    </Box>
                </Box>

                {loading ? (
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2 }}>
                        <CircularProgress sx={{ color: C.gold }} size={44} thickness={3} />
                        <Typography sx={{ fontSize: '0.82rem', color: C.textMuted }}>
                            Zahlung wird verarbeitet…
                        </Typography>
                    </Box>
                ) : (
                    <Box sx={{
                        p: 2,
                        borderRadius: '14px',
                        bgcolor: C.bg2,
                        border: `1px solid ${C.borderSubtle}`,
                    }}>
                        <PayPalButtons
                            style={{ layout: 'vertical', color: 'gold', shape: 'rect', label: 'pay' }}
                            createOrder={createOrder}
                            onApprove={onApprove}
                            onError={onError}
                            onCancel={onCancel}
                        />
                    </Box>
                )}
            </Box>
        </Box>
    );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function PremiumModal({ open, onClose, nutzerId, onPremiumActivated }) {
    const [plan, setPlan] = useState('yearly');
    const [step, setStep] = useState('select'); // checking | select | payment | success | error | already_premium
    const [loading, setLoading] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    // ── Beim Öffnen: aktuellen Premium-Status prüfen ──
    useEffect(() => {
        if (!open) return;

        setErrorMsg('');
        setStep('checking');

        let active = true;

        PremiumApi.getStatus(nutzerId)
            .then((res) => {
                if (!active) return;
                if (res.data?.is_premium) {
                    setStep('already_premium');
                } else {
                    setStep('select');
                }
            })
            .catch((err) => {
                console.error('Status-Check fehlgeschlagen:', err);
                if (!active) return;
                // Im Zweifel die Auswahl anzeigen, nicht blockieren
                setStep('select');
            });

        return () => { active = false; };
    }, [open, nutzerId]);

    const createOrder = useCallback((data, actions) => {
        return actions.order.create({
            purchase_units: [{
                amount: { value: PLANS[plan].priceValue, currency_code: 'EUR' },
                description: `GymApp Premium – ${PLANS[plan].label}`,
            }],
        });
    }, [plan]);

    const onApprove = useCallback(async (data, actions) => {
        setLoading(true);
        try {
            await PremiumApi.activatePremium({ nutzerId, plan, paypalOrderId: data.orderID });
            setStep('success');
            onPremiumActivated?.();
        } catch (err) {
            console.error(err);
            if (err.response?.status === 409) {
                setErrorMsg('Du hast bereits ein aktives Abo. Falls eine Zahlung erfolgt ist, kontaktiere bitte den Support.');
            } else {
                setErrorMsg('Zahlung konnte nicht abgeschlossen werden. Bitte versuche es erneut.');
            }
            setStep('error');
        } finally {
            setLoading(false);
        }
    }, [nutzerId, plan, onPremiumActivated]);

    const onError = useCallback((err) => {
        console.error(err);
        setErrorMsg('Zahlung fehlgeschlagen. Bitte versuche es erneut.');
        setStep('error');
    }, []);

    const closableSteps = ['select', 'success', 'already_premium', 'checking', 'error'];

    return (
        <ThemeProvider theme={darkTheme}>
            <PayPalScriptProvider options={{
                'client-id': process.env.REACT_APP_PAYPAL_CLIENT_ID,
                currency: 'EUR',
            }}>
                {/* ── Payment: eigenes Fullscreen-Overlay außerhalb des Dialogs ── */}
                <PaymentOverlay
                    open={step === 'payment'}
                    plan={plan}
                    loading={loading}
                    onBack={() => setStep('select')}
                    createOrder={createOrder}
                    onApprove={onApprove}
                    onError={onError}
                    onCancel={() => setStep('select')}
                />

                {/* ── Dialog: für select / success / error / already_premium / checking ── */}
                <Dialog
                    open={open && step !== 'payment'}
                    onClose={closableSteps.includes(step) ? onClose : undefined}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: {
                            background: `linear-gradient(160deg, ${C.bg1} 0%, ${C.bg0} 100%)`,
                            border: `1px solid ${C.border}`,
                            borderRadius: '22px',
                            maxHeight: 'calc(100dvh - 32px)',
                            mx: 1.5,
                            overflow: 'hidden',
                            boxShadow: `0 0 0 1px rgba(0,0,0,0.5), 0 28px 64px rgba(0,0,0,0.65), 0 0 60px rgba(212,175,55,0.08)`,
                        },
                    }}
                    slotProps={{
                        backdrop: { sx: { backdropFilter: 'blur(6px)', bgcolor: 'rgba(0,0,0,0.75)' } }
                    }}
                >
                    <ModalHeader
                        onClose={onClose}
                        showClose={closableSteps.includes(step)}
                    />

                    <DialogContent sx={{
                        p: 0, overflowY: 'auto', bgcolor: C.bg1,
                        '&::-webkit-scrollbar': { width: 4 },
                        '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
                        '&::-webkit-scrollbar-thumb': { bgcolor: C.border, borderRadius: 2 },
                    }}>
                        <Box sx={{ px: 2.5, pt: 2.5, pb: 3 }}>

                            {/* ── CHECKING ── */}
                            {step === 'checking' && (
                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 8, gap: 2, animation: 'fadeUp 0.25s ease both' }}>
                                    <CircularProgress sx={{ color: C.gold }} size={36} thickness={3} />
                                    <Typography sx={{ fontSize: '0.82rem', color: C.textMuted }}>
                                        Status wird geprüft…
                                    </Typography>
                                </Box>
                            )}

                            {/* ── ALREADY PREMIUM ── */}
                            {step === 'already_premium' && (
                                <Box sx={{ textAlign: 'center', py: 4, animation: 'fadeUp 0.3s ease both' }}>
                                    <Box sx={{
                                        width: 80, height: 80, borderRadius: '50%',
                                        bgcolor: `${C.gold}18`,
                                        border: `2px solid ${C.goldDim}`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        mx: 'auto', mb: 2.5,
                                        animation: 'successPop 0.4s ease both',
                                    }}>
                                        <WorkspacePremiumIcon sx={{ fontSize: '2.5rem', color: C.gold }} />
                                    </Box>
                                    <Typography sx={{ ...shimmerText, fontSize: '1.3rem', fontWeight: 800, mb: 1 }}>
                                        Du bist bereits Premium!
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.85rem', color: C.textMuted, mb: 3.5 }}>
                                        Du hast bereits Zugriff auf alle Premium-Features. Viel Erfolg beim Training! 💪
                                    </Typography>
                                    <Button fullWidth onClick={onClose} sx={goldBtn}>
                                        Schließen
                                    </Button>
                                </Box>
                            )}

                            {/* ── SUCCESS ── */}
                            {step === 'success' && (
                                <Box sx={{ textAlign: 'center', py: 4, animation: 'fadeUp 0.3s ease both' }}>
                                    <Box sx={{
                                        width: 80, height: 80, borderRadius: '50%',
                                        bgcolor: `${C.green}18`,
                                        border: `2px solid ${C.green}55`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        mx: 'auto', mb: 2.5,
                                        animation: 'successPop 0.4s ease both',
                                    }}>
                                        <CheckCircleIcon sx={{ fontSize: '2.5rem', color: C.green }} />
                                    </Box>
                                    <Typography sx={{ ...shimmerText, fontSize: '1.3rem', fontWeight: 800, mb: 1 }}>
                                        Premium aktiviert!
                                    </Typography>
                                    <Typography sx={{ fontSize: '0.85rem', color: C.textMuted, mb: 3.5 }}>
                                        Du hast jetzt Zugriff auf alle Premium-Features. Viel Erfolg beim Training! 💪
                                    </Typography>
                                    <Button fullWidth onClick={onClose} sx={goldBtn}>
                                        Los geht's
                                    </Button>
                                </Box>
                            )}

                            {/* ── ERROR ── */}
                            {step === 'error' && (
                                <Box sx={{ py: 2, animation: 'fadeUp 0.3s ease both' }}>
                                    <Box sx={{
                                        p: 2, borderRadius: '12px',
                                        bgcolor: 'rgba(239,68,68,0.08)',
                                        border: '1px solid rgba(239,68,68,0.25)',
                                        mb: 2.5,
                                    }}>
                                        <Typography sx={{ fontSize: '0.82rem', color: '#fca5a5', textAlign: 'center' }}>
                                            {errorMsg}
                                        </Typography>
                                    </Box>
                                    <Button fullWidth onClick={() => setStep('select')} sx={primaryBtn}>
                                        Zurück zur Auswahl
                                    </Button>
                                </Box>
                            )}

                            {/* ── SELECT ── */}
                            {step === 'select' && (
                                <Box sx={{ animation: 'fadeUp 0.25s ease both' }}>
                                    <Typography sx={{
                                        fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.1em',
                                        color: C.textFaint, textTransform: 'uppercase', mb: 1.25,
                                    }}>
                                        Enthaltene Features
                                    </Typography>

                                    <Box
                                        sx={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'stretch',
                                            gap: 0.75,
                                            mb: 3,
                                            width: '100%'
                                        }}
                                    >
                                        {FEATURES.map((f, i) => (
                                            <FeaturePill
                                                key={f.label}
                                                icon={f.icon}
                                                label={f.label}
                                                delay={i * 0.04}
                                                fullWidth
                                            />
                                        ))}
                                    </Box>

                                    <Box sx={{
                                        height: '1px',
                                        background: `linear-gradient(90deg, transparent, ${C.gold}44, transparent)`,
                                        mb: 2.5,
                                    }} />

                                    <Typography sx={{
                                        fontSize: '0.65rem', fontWeight: 700, letterSpacing: '0.1em',
                                        color: C.textFaint, textTransform: 'uppercase', mb: 1.75,
                                    }}>
                                        Laufzeit wählen
                                    </Typography>

                                    <Box sx={{ display: 'flex', gap: 1.5, mb: 3 }}>
                                        {Object.entries(PLANS).map(([key, p]) => (
                                            <PlanCard key={key} planKey={key} plan={p} selected={plan} onSelect={setPlan} />
                                        ))}
                                    </Box>

                                    <Box sx={{ position: 'relative', overflow: 'hidden', borderRadius: '14px', mb: 1.5 }}>
                                        <Button fullWidth size="large" onClick={() => setStep('payment')} sx={goldBtn}>
                                            Weiter zur Zahlung · {PLANS[plan].price}
                                        </Button>
                                        <Box sx={{
                                            position: 'absolute', top: 0, left: '-100%',
                                            width: '60%', height: '100%',
                                            background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.18), transparent)',
                                            animation: 'shimmerSweep 5s ease-in-out infinite',
                                            pointerEvents: 'none',
                                        }} />
                                    </Box>

                                    <Typography sx={{ textAlign: 'center', color: C.textFaint, fontSize: '0.7rem' }}>
                                        Jederzeit kündbar · Sichere Zahlung via PayPal
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </DialogContent>
                </Dialog>
            </PayPalScriptProvider>
        </ThemeProvider>
    );
}